import { useNavigate, useParams } from 'react-router-dom';

import { sendReceiptByEmail } from 'api/Transaction';

import { cn } from 'lib/helpers/utility';
import useModal from 'lib/hooks/useModal';
import { isKioskDevice } from 'lib/helpers/kiosk';
import { useUIContext } from 'lib/context/UIContext/UIContext';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import Typography from 'components/shared/Typography/Typography';
import ReceiptModal from 'components/shared/Receipt/ReceiptModal';

const SuccessPayment = ({ transactionId, isKiosk, token: tokenProp }) => {
  const { isOpen, openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const params = useParams();
  const token = tokenProp ?? params.token;
  const { device } = useUIContext();
  const kiosk = isKioskDevice();

  const emailReceipt = async () => {
    try {
      const res = await sendReceiptByEmail(navigate, { transactionId, token });
      if (res?.code === 0) {
        showAlert({
          color: 'success',
          title: 'Receipt emailed',
          position: device === 'laptop' || device === 'desktop' ? 'top-right' : 'bottom-right'
        });
      } else {
        showAlert({
          color: 'danger',
          title: "Couldn't email receipt. Please try again.",
          position: device === 'laptop' || device === 'desktop' ? 'top-right' : 'bottom-right'
        });
      }
    } catch (error) {
      showAlert({
        color: 'danger',
        title: "Couldn't email receipt. Please try again.",
        position: device === 'laptop' || device === 'desktop' ? 'top-right' : 'bottom-right'
      });
    }
  };

  return (
    <div className="fixed inset-0 h-screen w-screen overflow-hidden p-10">
      <Icon
        icon="bg-pattern"
        className="absolute inset-0 h-full w-full [&>div]:absolute [&>div]:inset-0 [&>div]:h-full [&>div]:w-full"
      />
      <div
        className={cn(
          'relative flex h-full w-full flex-col items-center justify-center',
          kiosk ? 'gap-6' : 'gap-3'
        )}>
        <div className="flex flex-col items-center justify-center">
          <Icon icon="illustration-payment-successful" size={kiosk ? 220 : 160} />
          <Typography
            variant="p"
            size="medium"
            weight={kiosk ? 'semibold' : 'medium'}
            className={kiosk ? '!text-3xl' : undefined}>
            Payment successful
          </Typography>
          <Typography
            variant="p"
            size="small"
            color="neutral-700"
            className={kiosk ? '!text-lg' : undefined}>
            The payment was processed successfully.
          </Typography>
        </div>
        <div
          className={cn(
            'flex flex-col items-center justify-center',
            kiosk ? 'w-full max-w-[600px] gap-4' : 'min-w-[280px] gap-2'
          )}>
          <div
            className={cn(
              'flex w-full items-center sm:flex-col',
              kiosk ? 'mt-4 gap-4' : 'mt-3 gap-2'
            )}>
            <Button
              text="View receipt"
              icon="receipt-1-mono"
              variant={kiosk ? 'secondary' : 'tertiary'}
              size={kiosk ? 'large' : 'small'}
              color={kiosk ? 'primary' : 'neutral'}
              iconColor={kiosk ? 'primary' : undefined}
              iconSize={kiosk ? 26 : undefined}
              textClassName={cn('whitespace-nowrap', kiosk && 'text-xl')}
              wrapperClassName="w-full"
              className={cn('w-full', kiosk && 'h-16 rounded-xl')}
              onClick={openModal}
            />
            <Button
              text="Email receipt"
              icon="mail-mono"
              variant={kiosk ? 'secondary' : 'tertiary'}
              size={kiosk ? 'large' : 'small'}
              color={kiosk ? 'primary' : 'neutral'}
              iconColor={kiosk ? 'primary' : undefined}
              iconSize={kiosk ? 26 : undefined}
              textClassName={cn('whitespace-nowrap', kiosk && 'text-xl')}
              wrapperClassName="w-full"
              className={cn('w-full', kiosk && 'h-16 rounded-xl')}
              onClick={emailReceipt}
            />
          </div>
          <Button
            text="Close"
            variant={kiosk ? 'primary' : 'tertiary'}
            size={kiosk ? 'large' : 'small'}
            textClassName={kiosk ? 'text-xl' : undefined}
            wrapperClassName="w-full"
            className={cn(
              'w-full',
              kiosk &&
                'h-16 rounded-xl shadow-[0_10px_24px_-8px_rgba(11,133,201,0.5)] transition-transform active:scale-[0.98]'
            )}
            onClick={() => navigate(isKiosk ? '/kiosk/idle' : '/')}
          />
        </div>
      </div>
      <ReceiptModal
        transactionId={transactionId}
        isOpen={isOpen}
        onClose={closeModal}
        token={token}
      />
    </div>
  );
};

export default SuccessPayment;
